import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import InvestorPatientDetail from './InvestorPatientDetail';
import { RelacionBadge } from './estadoRelacion';

interface InvestorPatient {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string | null;
  rut: string | null;
  prevision: string | null;
  activo: boolean;
  estado_relacion: string;
  total_consultas: number;
  ultima_consulta: string | null;
  nombre_terapeuta: string | null;
  porcentaje_inversionista: number;
  consulta_inicio_split: number;
}

interface InvestorPatientsProps {
  investor: { id: string };
  // Sólo el inversor fundador puede editar el estado de relación (gate
  // isJuanPabloInvestor en TherapistPortal.tsx). El resto lo ve solo-lectura.
  isFounder?: boolean;
}

const previsionLabel: Record<string, string> = {
  particular: 'Particular',
  isapre: 'Isapre',
  fonasa: 'Fonasa',
};

const InvestorPatients: React.FC<InvestorPatientsProps> = ({ investor, isFounder = false }) => {
  const [patients, setPatients] = useState<InvestorPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<InvestorPatient | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase.rpc('get_investor_patients', {
        p_inversionista_id: investor.id,
      });
      if (rpcError) {
        console.error('Error cargando pacientes inversor:', rpcError.code);
        setError('No se pudo cargar la lista de pacientes.');
      } else {
        setPatients((data as InvestorPatient[]) ?? []);
      }
      setLoading(false);
    };
    load();
  }, [investor.id]);

  const filtered = patients.filter(p => {
    const q = search.toLowerCase();
    return (
      p.nombre.toLowerCase().includes(q) ||
      p.apellido.toLowerCase().includes(q) ||
      (p.email?.toLowerCase().includes(q) ?? false) ||
      (p.rut?.toLowerCase().includes(q) ?? false)
    );
  });

  const getInitials = (nombre: string, apellido: string) =>
    `${nombre[0] ?? ''}${apellido[0] ?? ''}`.toUpperCase();

  const AVATAR_COLORS = [
    { bg: 'rgba(0, 122, 255, 0.12)',  text: '#0071e3' },
    { bg: 'rgba(52, 199, 89, 0.12)',  text: '#28a745' },
    { bg: 'rgba(255, 149, 0, 0.12)',  text: '#C05E00' },
    { bg: 'rgba(107, 33, 168, 0.12)', text: '#6B21A8' },
    { bg: 'rgba(192, 94, 0, 0.12)',   text: '#C05E00' },
  ];
  const avatarColor = (id: string) => AVATAR_COLORS[id.charCodeAt(0) % AVATAR_COLORS.length];

  if (selectedPatient) {
    return (
      <InvestorPatientDetail
        investor={investor}
        patient={selectedPatient}
        isFounder={isFounder}
        onBack={() => setSelectedPatient(null)}
        onEstadoChange={(patientId, nuevo) => {
          setPatients(prev => prev.map(p => (p.id === patientId ? { ...p, estado_relacion: nuevo } : p)));
          setSelectedPatient(prev => (prev && prev.id === patientId ? { ...prev, estado_relacion: nuevo } : prev));
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando pacientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-management">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px' }}>
          Mis Pacientes
        </h2>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
          {patients.length} paciente{patients.length !== 1 ? 's' : ''} en tu cartera
        </p>
      </div>

      {patients.length > 0 && (
        <div className="search-bar" style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <i className="fas fa-search" style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-muted)', fontSize: 14,
            }}></i>
            <input
              type="text"
              placeholder="Buscar por nombre, RUT o email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '9px 12px 9px 36px',
                border: '1px solid rgba(0,0,0,0.12)', borderRadius: 10,
                fontSize: 14, color: 'var(--text-primary)',
                background: 'var(--bg-primary)', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: 16, background: '#FFF2F0', border: '1px solid #FFCCC7', borderRadius: 10, color: '#CF1322', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!error && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
          <i className="fas fa-user-slash" style={{ fontSize: 40, marginBottom: 12, display: 'block' }}></i>
          {search ? 'Sin resultados para esta búsqueda.' : 'Aún no tenés pacientes en tu cartera.'}
        </div>
      )}

      <div className="patients-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filtered.map(patient => (
          <button
            key={patient.id}
            type="button"
            className="patient-card patient-card--btn"
            aria-label={`Ver historial de sesiones de ${patient.nombre} ${patient.apellido}`}
            onClick={() => setSelectedPatient(patient)}
            style={{
              background: 'var(--bg-primary)', borderRadius: 14,
              padding: '16px 20px', boxShadow: 'var(--shadow-card)',
              display: 'flex', alignItems: 'flex-start', gap: 16,
              border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer',
              font: 'inherit',
            }}
          >
            <div className="patient-avatar--initials" style={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: avatarColor(patient.id).bg,
              color: avatarColor(patient.id).text,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 16,
            }}>
              {getInitials(patient.nombre, patient.apellido)}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>
                  {patient.nombre} {patient.apellido}
                </span>
                <RelacionBadge estado={patient.estado_relacion} />
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 4 }}>
                {patient.rut && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    <i className="fas fa-id-card" style={{ marginRight: 4 }}></i>{patient.rut}
                  </span>
                )}
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  <i className="fas fa-envelope" style={{ marginRight: 4 }}></i>{patient.email}
                </span>
                {patient.telefono && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    <i className="fas fa-phone" style={{ marginRight: 4 }}></i>{patient.telefono}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 6 }}>
                {patient.nombre_terapeuta && (
                  <span style={{ fontSize: 13, color: 'var(--apple-blue)' }}>
                    <i className="fas fa-user-md" style={{ marginRight: 4 }}></i>{patient.nombre_terapeuta}
                  </span>
                )}
                {patient.prevision && (
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    <i className="fas fa-shield-alt" style={{ marginRight: 4 }}></i>
                    {previsionLabel[patient.prevision] ?? patient.prevision}
                  </span>
                )}
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  <i className="fas fa-clipboard-list" style={{ marginRight: 4 }}></i>
                  {patient.total_consultas} sesion{patient.total_consultas !== 1 ? 'es' : ''}
                </span>
                {patient.ultima_consulta && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    <i className="fas fa-calendar-check" style={{ marginRight: 4 }}></i>
                    Última: {new Date(patient.ultima_consulta).toLocaleDateString('es-CL', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </span>
                )}
              </div>
            </div>

            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(52, 199, 89, 0.12)',
                color: 'var(--success-text, #057A55)',
                fontSize: 15, fontWeight: 700,
                marginBottom: 4,
              }}>
                {patient.porcentaje_inversionista}%
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                desde sesión {patient.consulta_inicio_split}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default InvestorPatients;
